
import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useAuth } from "@/hooks/use-auth";
import { ShoppingCart } from "lucide-react";

interface PurchaseRequestDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  productId: string;
  professionalId: string;
  productTitle?: string;
  productPrice?: number;
}

export const PurchaseRequestDialog = ({ 
  open, 
  onOpenChange, 
  productId,
  professionalId,
  productTitle,
  productPrice 
}: PurchaseRequestDialogProps) => {
  const [quantity, setQuantity] = useState(1);
  const [notes, setNotes] = useState("");
  const [pickupAddress, setPickupAddress] = useState("");
  const { session } = useAuth();
  const queryClient = useQueryClient();

  const sendPurchaseRequest = useMutation({
    mutationFn: async () => {
      if (!session?.user?.id) {
        throw new Error("Vous devez être connecté pour faire une demande d'achat");
      }

      if (!professionalId || !productId) {
        throw new Error("Informations produit manquantes");
      }

      if (!pickupAddress.trim()) {
        throw new Error("L'adresse de récupération/livraison est requise");
      }

      console.log("Sending purchase request:", {
        buyer_id: session.user.id,
        professional_id: professionalId,
        product_id: productId,
        quantity,
        notes: notes.trim() || null,
        pickup_address: pickupAddress.trim()
      });

      const { data, error } = await supabase
        .from("product_purchase_requests")
        .insert({
          buyer_id: session.user.id,
          professional_id: professionalId,
          product_id: productId,
          quantity,
          notes: notes.trim() || null,
          pickup_address: pickupAddress.trim(),
          status: 'pending'
        })
        .select()
        .single();

      if (error) {
        console.error("Error sending purchase request:", error);
        throw new Error(`Erreur lors de l'envoi: ${error.message}`);
      }

      console.log("Purchase request sent successfully:", data);
      return data;
    },
    onSuccess: () => {
      toast.success("Demande d'achat envoyée avec succès !");
      // Invalider les requêtes liées pour rafraîchir les données
      queryClient.invalidateQueries({ queryKey: ["purchase-requests"] });
      
      // Réinitialiser le formulaire
      setQuantity(1);
      setNotes("");
      setPickupAddress("");
      onOpenChange(false);
    },
    onError: (error: any) => {
      console.error("Error sending purchase request:", error);
      
      let errorMessage = "Erreur lors de l'envoi de la demande d'achat. Veuillez réessayer.";
      
      if (error.message?.includes("connecté")) {
        errorMessage = "Vous devez être connecté pour faire une demande d'achat";
      } else if (error.message?.includes("manquantes")) {
        errorMessage = "Erreur: Informations produit manquantes";
      } else if (error.message?.includes("requise")) {
        errorMessage = "L'adresse de récupération/livraison est requise";
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session?.user?.id) {
      toast.error("Vous devez être connecté pour faire une demande d'achat");
      return;
    }

    if (!professionalId || !productId) {
      toast.error("Erreur: Informations produit manquantes");
      return;
    }

    if (quantity < 1) {
      toast.error("La quantité doit être d'au moins 1");
      return;
    }

    if (!pickupAddress.trim()) {
      toast.error("L'adresse de récupération/livraison est requise");
      return;
    }

    sendPurchaseRequest.mutate();
  };

  // Vérifier si l'utilisateur est connecté
  if (!session?.user?.id) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5" />
              Connexion requise
            </DialogTitle>
            <DialogDescription>
              Vous devez être connecté pour faire une demande d'achat.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              onClick={() => onOpenChange(false)}
              variant="outline"
            >
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShoppingCart className="h-5 w-5" />
            Demande d'achat
          </DialogTitle>
          <DialogDescription>
            {productTitle && (
              <span className="block mt-1">
                Produit: {productTitle}
                {productPrice && <span className="block text-sm text-muted-foreground">Prix: {productPrice}€</span>}
              </span>
            )}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit}>
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="quantity">Quantité</Label>
              <Input
                id="quantity"
                type="number"
                min="1"
                value={quantity}
                onChange={(e) => setQuantity(parseInt(e.target.value) || 1)}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="pickup-address">Adresse de récupération/livraison *</Label>
              <Textarea
                id="pickup-address"
                placeholder="Indiquez votre adresse pour la livraison ou récupération..."
                value={pickupAddress}
                onChange={(e) => setPickupAddress(e.target.value)}
                rows={2}
                required
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optionnel)</Label>
              <Textarea
                id="notes"
                placeholder="Ajoutez des commentaires ou des demandes spéciales..."
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              type="button" 
              variant="outline" 
              onClick={() => onOpenChange(false)}
              disabled={sendPurchaseRequest.isPending}
            >
              Annuler
            </Button>
            <Button 
              type="submit" 
              disabled={sendPurchaseRequest.isPending}
            >
              {sendPurchaseRequest.isPending ? "Envoi..." : "Envoyer la demande"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
